/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface FolderProps {
    /**
     * A string representing the CSS class to be applied to the FolderIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the FolderIcon element.
     *
     * @default 22
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Folder'
     */
    testId?: string;
    /**
     * A number or string representing the height of the FolderIcon element.
     *
     * @default 25
     */
    width?: number | string;
}

/**
 * The `Folder` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `FolderProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the FolderIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the FolderIcon element.
 * @param props.ref       A React ref object that allows access to the underlying SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the FolderIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const FolderComponent = <Folder className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Folder = ({
    className = '',
    color1 = '#0050FF',
    height = 20,
    ref,
    testId = 'Folder',
    width = 22
}: FolderProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 22 20"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M21.5 19.2812H0.5V2.87875C0.5 1.70875 1.50875 0.71875 2.70125 0.71875H6.43625C6.995 0.71875 7.52375 0.9325 7.925 1.315L9.995 3.29125C10.1525 3.44125 10.3588 3.52375 10.5763 3.52375H19.2875C20.4837 3.52375 21.4963 4.51375 21.4963 5.68375V19.2812H21.5ZM1.8125 17.9688H20.1875V5.68375C20.1875 5.23375 19.7675 4.83625 19.2913 4.83625H10.58C10.0212 4.83625 9.49625 4.6225 9.09125 4.24L7.02125 2.26375C6.86375 2.11375 6.6575 2.03125 6.44 2.03125H2.70125C2.22875 2.03125 1.8125 2.425 1.8125 2.87875V17.9688Z" fill={color1} />
    </svg>
);

Folder.displayName = 'Folder';